'use client';

import { useEffect, useRef, useState, type JSX, Children } from 'react';

import clsx from 'clsx';
import type { FieldPath, FieldValues, UseFormRegisterReturn } from 'react-hook-form';
import type {
    ClearIndicatorProps,
    ContainerProps,
    ControlProps,
    DropdownIndicatorProps,
    GroupBase,
    GroupHeadingProps,
    GroupProps,
    IndicatorsContainerProps,
    InputProps,
    MenuListProps,
    MenuPlacement,
    MenuProps,
    NoticeProps,
    OptionProps,
    PlaceholderProps,
    ValueContainerProps,
} from 'react-select';
import ReactSelect, { components } from 'react-select';

import { horizontalPaddingClasses } from '@/components/classes/horizontalPaddingClasses';
import { inputBackgroundClasses } from '@/components/classes/inputBackgroundClasses';
import { inputBorderClasses } from '@/components/classes/inputBorderClasses';
import { placeholderClasses } from '@/components/classes/inputPlaceholderClasses';
import { inputTextSizeClasses } from '@/components/classes/inputTextSizeClasses';
import { textColorClasses } from '@/components/classes/textColorClasses';
import { verticalPaddingClasses } from '@/components/classes/verticalPaddingClasses';
import { testIds } from '@/components/constants/testIds';
import { composeEndRadiusClasses } from '@/components/functions/composeEndRadiusClasses';
import { composeRadiusClasses } from '@/components/functions/composeRadiusClasses';
import { composeShadowClasses } from '@/components/functions/composeShowClasses';
import { composeStartRadiusClasses } from '@/components/functions/composeStartRadiusClasses';
import { composeWidthClasses } from '@/components/functions/composeWidthClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { AffixBackground } from '@/components/types/AffixBackground';
import type { Color } from '@/components/types/Color';
import type { IconType } from '@/components/types/IconType';
import type { InputBackground } from '@/components/types/InputBackground';
import type { InputBorder } from '@/components/types/InputBorder';
import type { InputTextSize } from '@/components/types/InputTextSize';
import type { PaddingSize } from '@/components/types/PaddingSize';
import type { Radius } from '@/components/types/Radius';
import type { Shadow } from '@/components/types/Shadow';
import type { Width } from '@/components/types/Width';
import Text from '@/components/ui/Text';
import { componentsConfig } from '@/config/componentsConfig';
import appIcons from '@/constants/appIcons';
import useI18n from '@/hooks/useI18n';

import Button from '../Button';
import HStack from '../HStack';
import Icon from '../Icon';
import InputAffix from '../InputAffix';
import Spinner from '../Spinner';
import VStack from '../VStack';

type NewSelectProps<T extends FieldValues, G extends GroupBase<T> = { options: T[] }> = {
    options: (T | G)[];
    value?: T | null;
    onChange?: (option: T | null) => void;
    renderOption?: (option: T) => JSX.Element;
    renderSelectedOption?: (value: T) => JSX.Element;
    renderGroupLabel?: (group: G) => JSX.Element;
    dismissOnChange?: boolean;
    menuTitle?: string;
    menuTitleIcon?: IconType;
    menuPlacement?: MenuPlacement;
    optionIdKey?: keyof T;
    optionTitleKey?: keyof T;
    optionGroupKey?: keyof G;
    optionTitlePrefix?: string;
    optionTitleSuffix?: string;
    noOptionsIcon?: IconType;
    noOptionsTitle?: string;
    noOptionsMessage?: string;
    isLoading?: boolean;
    isClearable?: boolean;
    label?: string;
    name?: string;
    placeholder?: string;
    prefixText?: string;
    prefixIcon?: IconType;
    prefixColor?: Color;
    prefixBackground?: AffixBackground;
    invalidPrefixBackground?: AffixBackground;
    prefixClassName?: string;
    suffixText?: string;
    suffixIcon?: IconType;
    suffixColor?: Color;
    suffixBackground?: AffixBackground;
    invalidSuffixBackground?: AffixBackground;
    suffixClassName?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    isCreatable?: boolean;
    maxDisplayedItems?: number;
    createButtonTitle?: string;
    createButtonAction?: () => void;
    errorMessage?: string;
    radius?: Adaptive<Radius>;
    width?: Adaptive<Width>;
    textSize?: InputTextSize;
    verticalPadding?: PaddingSize;
    horizontalPadding?: PaddingSize;
    shadow?: Adaptive<Shadow>;
    color?: Color;
    background?: InputBackground;
    border?: InputBorder;
    menuPortalTarget?: HTMLElement;
    className?: string;
    inputClassName?: string;
    register?: UseFormRegisterReturn<FieldPath<T>>;
};

export default function NewSelect<T extends FieldValues, G extends GroupBase<T> = { options: T[] }>({
    options,
    value,
    onChange,
    renderOption,
    renderSelectedOption,
    renderGroupLabel,
    menuPlacement = componentsConfig.select.menuPlacement,
    dismissOnChange = componentsConfig.select.dismissOnChange,
    menuTitle,
    menuTitleIcon,
    optionIdKey = 'id' as keyof T,
    optionTitleKey = 'title' as keyof T,
    optionGroupKey = 'label' as keyof G,
    optionTitlePrefix,
    optionTitleSuffix,
    noOptionsIcon,
    noOptionsTitle,
    noOptionsMessage,
    isLoading = componentsConfig.select.isLoading,
    isClearable = componentsConfig.select.isClearable,
    label,
    placeholder,
    prefixText,
    prefixIcon,
    prefixClassName,
    prefixColor = componentsConfig.inputAffix.color,
    prefixBackground = componentsConfig.inputAffix.background,
    invalidPrefixBackground = componentsConfig.inputAffix.invalidBackground,
    suffixText,
    suffixIcon,
    suffixClassName,
    suffixColor = componentsConfig.inputAffix.color,
    suffixBackground = componentsConfig.inputAffix.background,
    invalidSuffixBackground = componentsConfig.inputAffix.invalidBackground,
    isDisabled,
    isRequired,
    isCreatable = false,
    maxDisplayedItems = componentsConfig.select.maxDisplayedItems,
    createButtonTitle,
    createButtonAction,
    errorMessage,
    radius = componentsConfig.select.radius,
    width = componentsConfig.select.width,
    verticalPadding = componentsConfig.select.verticalPadding,
    horizontalPadding = componentsConfig.select.horizontalPadding,
    textSize = componentsConfig.select.textSize,
    shadow = componentsConfig.select.shadow,
    color = componentsConfig.select.color,
    background = componentsConfig.select.background,
    border = componentsConfig.textInput.border,
    menuPortalTarget,
    className,
    inputClassName,
    register,
}: NewSelectProps<T, G>): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const ref = useRef(null);
    const { t } = useI18n();

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const requiredIndicator = isRequired ? '*' : '';

    const basicInputClasses = 'w-full  focus:outline-none';
    const disabledClasses = 'opacity-60';

    const hoverBackgroundClasses = {
        accent: 'hover:bg-accent-500/15 dark:hover:bg-accent-500/15 hover:!text-accent-500 dark:hover:!text-accent-500 cursor-pointer',
        info: 'hover:bg-info-600/15 dark:hover:bg-info-600/15 hover:!text-info-600 dark:hover:!text-info-600 cursor-pointer',
        success: 'hover:bg-success-600/15 dark:hover:bg-success-600/15 hover:!text-success-600 dark:hover:!text-success-600 cursor-pointer',
        warning: 'hover:bg-warning-600/15 dark:hover:bg-warning-600/15 hover:!text-warning-600 dark:hover:!text-warning-600 cursor-pointer',
        danger: 'hover:bg-danger-600/15 dark:hover:bg-danger-600/15 hover:!text-danger-600 dark:hover:!text-danger-600 cursor-pointer',
        primary: 'hover:bg-slate-950/10 dark:hover:bg-slate-950/75 hover:!text-slate-950 dark:hover:!text-slate-950 cursor-pointer',
        secondary: 'hover:bg-slate-700/75 dark:hover:bg-slate-700/75 hover:!text-slate-700 dark:hover:!text-slate-700 cursor-pointer',
        tertiary: 'hover:bg-slate-600/75 dark:hover:bg-slate-600/75 hover:!text-slate-600 dark:hover:!text-slate-600 cursor-pointer',
        quaternary: 'hover:bg-slate-500/75 dark:hover:bg-slate-500/75 hover:!text-slate-500 dark:hover:!text-slate-500 cursor-pointer',
        black: 'hover:bg-black hover:!text-black cursor-pointer',
        white: 'hover:bg-white hover:!text-slate-950 cursor-pointer',
        none: '',
    };

    const focusedBackgroundClasses = {
        accent: '!bg-accent-500/15 dark:!bg-accent-500/15 !text-accent-500 dark:!text-accent-500 cursor-pointer',
        info: '!bg-info-600/15 dark:!bg-info-600/15 !text-info-600 dark:!text-info-600 cursor-pointer',
        success: '!bg-success-600/15 dark:!bg-success-600/15 !text-success-600 dark:!text-success-600 cursor-pointer',
        warning: '!bg-warning-600/15 dark:!bg-warning-600/15 !text-warning-600 dark:!text-warning-600 cursor-pointer',
        danger: '!bg-danger-600/15 dark:!bg-danger-600/15 !text-danger-600 dark:!text-danger-600 cursor-pointer',
        primary: '!bg-slate-950/10 dark:!bg-slate-950/75 !text-slate-950 dark:!text-slate-950 cursor-pointer',
        secondary: '!bg-slate-700/75 dark:!bg-slate-700/75 !text-slate-700 dark:!text-slate-700 cursor-pointer',
        tertiary: '!bg-slate-600/75 dark:!bg-slate-600/75 !text-slate-600 dark:!text-slate-600 cursor-pointer',
        quaternary: '!bg-slate-500/75 dark:!bg-slate-500/75 !text-slate-500 dark:!text-slate-500 cursor-pointer',
        black: '!bg-black hover:!text-black cursor-pointer',
        white: '!bg-white hover:!text-slate-950 cursor-pointer',
        none: '',
    };

    const activeBackgroundClasses = {
        accent: 'bg-accent-500 dark:bg-accent-500 !text-white cursor-pointer',
        info: 'bg-info-600 dark:bg-info-600 !text-white cursor-pointer',
        success: 'bg-success-600 dark:bg-success-600 !text-white cursor-pointer',
        warning: 'bg-warning-500 dark:bg-warning-500 !text-white cursor-pointer',
        danger: 'bg-danger-600 dark:bg-danger-600 !text-white cursor-pointer',
        primary: 'bg-slate-950 dark:bg-slate-950 !text-white dark:text-slate-950 cursor-pointer',
        secondary: 'bg-slate-700 dark:bg-slate-700 !text-white dark:text-slate-950 cursor-pointer',
        tertiary: 'bg-slate-600 dark:bg-slate-600 !text-white dark:text-slate-950 cursor-pointer',
        quaternary: 'bg-slate-500 dark:bg-slate-500 !text-white dark:text-slate-950 cursor-pointer',
        black: 'bg-slate-950 dark:bg-slate-950 !text-white dark:text-slate-950 cursor-pointer',
        white: 'bg-white dark:bg-white !text-slate-950 dark:text-slate-950 cursor-pointer',
        none: '',
    };

    const commonOptionClasses = 'block w-full p-3 rounded-lg';
    const inactiveOptionClasses = '';

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (dismissOnChange) {
            setIsMenuOpen(false);
        }
    }, [dismissOnChange, value]);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Mini Components UI
    // ----------------------------------------------------------------------------------------------------
    function SelectContainer(props: ContainerProps<T, boolean, G>): JSX.Element {
        return (
            <div
                className={clsx(
                    basicInputClasses,
                    inputTextSizeClasses[textSize],
                    border !== 'transparent' && inputBorderClasses[errorMessage ? 'danger' : border],
                    inputBackgroundClasses[errorMessage ? 'danger' : background],
                    placeholderClasses[errorMessage ? 'danger' : background],
                    composeStartRadiusClasses(radius),
                    composeEndRadiusClasses(radius),
                    isDisabled && disabledClasses
                )}
            >
                <components.SelectContainer {...props} />
            </div>
        );
    }

    function Control({ children, ...props }: ControlProps<T, boolean, G>): JSX.Element {
        return (
            <components.Control
                {...props}
                // className={clsx(
                //     'flex items-center',
                //     basicInputClasses,
                // inputTextSizeClasses[textSize],
                // border !== 'transparent' && inputBorderClasses[errorMessage ? 'danger' : border],
                // inputBackgroundClasses[errorMessage ? 'danger' : background],
                // placeholderClasses[errorMessage ? 'danger' : background],
                // composeStartRadiusClasses(radius),
                // composeEndRadiusClasses(radius),
                // inputPaddingSizeClasses[paddingSize],
                // isDisabled && disabledClasses,
                // inputClassName
                // )}
            >
                <HStack
                    className={clsx(
                        'items-stretch',
                        basicInputClasses,
                        composeRadiusClasses(radius),
                        composeShadowClasses(shadow),
                        inputTextSizeClasses[textSize],
                        border !== 'transparent' && inputBorderClasses[errorMessage ? 'danger' : border],
                        inputBackgroundClasses[errorMessage ? 'danger' : background],
                        placeholderClasses[errorMessage ? 'danger' : background],
                        composeStartRadiusClasses(radius),
                        composeEndRadiusClasses(radius),
                        verticalPaddingClasses[verticalPadding],
                        horizontalPaddingClasses[horizontalPadding],
                        isDisabled && disabledClasses,
                        inputClassName,
                        '-space-x-[1px]'
                    )}
                >
                    {/* Input Prefix */}
                    {(prefixIcon || prefixText) && (
                        <InputAffix
                            direction="start"
                            icon={prefixIcon}
                            text={prefixText}
                            textSize={textSize}
                            radius={radius}
                            border={border}
                            background={errorMessage ? invalidPrefixBackground : prefixBackground}
                            color={errorMessage ? 'danger' : prefixColor}
                            className={prefixClassName}
                        />
                    )}

                    {children}

                    {/* Input Suffix */}
                    {(suffixIcon || suffixText) && (
                        <InputAffix
                            direction="end"
                            icon={suffixIcon}
                            text={suffixText}
                            textSize={textSize}
                            radius={radius}
                            border={border}
                            background={errorMessage ? invalidSuffixBackground : suffixBackground}
                            color={errorMessage ? 'danger' : suffixColor}
                            className={suffixClassName}
                        />
                    )}
                </HStack>
            </components.Control>
        );
    }

    function DropdownIndicator(props: DropdownIndicatorProps<T, boolean, G>): JSX.Element {
        return (
            <components.DropdownIndicator {...props}>
                <Icon icon={appIcons.outline.dropdown} size={4} color={errorMessage ? 'danger' : 'tertiary'} />
            </components.DropdownIndicator>
        );
    }

    function ClearIndicator(props: ClearIndicatorProps<T, boolean, G>): JSX.Element {
        return (
            <components.ClearIndicator
                {...props}
                innerProps={{
                    ...props.innerProps,
                    onClick: (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        props.clearValue();
                    },
                    onMouseDown: (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                    },
                }}
            >
                <Icon icon={appIcons.outline.close} size={4} color={errorMessage ? 'danger' : 'tertiary'} />
            </components.ClearIndicator>
        );
    }

    function Input(props: InputProps<T, boolean, G>): JSX.Element {
        return (
            <components.Input
                {...props}
                className="cursor-pointer"
                aria-label={label ?? placeholder}
                aria-describedby={errorMessage ? 'error-message' : undefined}
                role="combobox"
            />
        );
    }

    function LoadingIndicator(): JSX.Element {
        return <Spinner size={4} color={errorMessage ? 'danger' : 'tertiary'} />;
    }

    function IndicatorsContainer(props: IndicatorsContainerProps<T, boolean, G>): JSX.Element {
        return (
            <components.IndicatorsContainer
                {...props}
                className={clsx('flex items-center space-x-0.5 cursor-pointer')} // Modified class
            />
        );
    }

    function Menu(props: MenuProps<T, boolean, G>): JSX.Element {
        return (
            <components.Menu<T, boolean, G>
                {...props}
                className="w-full backdrop-blur-md bg-white/90 dark:bg-neutral-900/75 z-30 mt-2 cursor-pointer rounded-xl border border-gray-50/75 dark:border-neutral-800 shadow-md p-1 space-y-2"
            >
                {(menuTitle || menuTitleIcon || isCreatable) && (
                    <HStack width="full" justify="between" alignment="center" className="px-3 pt-3 pb-1">
                        <HStack className="w-full" justify="start" alignment="center" hGap={1.5}>
                            {menuTitleIcon && <Icon icon={menuTitleIcon} color={color} size={4} />}
                            <Text size="sm" weight="medium" color="secondary">
                                {menuTitle}
                            </Text>
                        </HStack>

                        {isCreatable && createButtonAction && (
                            <Button
                                size="xs"
                                variant="link"
                                justify="end"
                                width="full"
                                prefixIcon={appIcons.outline.add}
                                prefixIconSize={3}
                                onClick={() => createButtonAction()}
                            >
                                {createButtonTitle ?? t('Components.Create_Option')}
                            </Button>
                        )}
                    </HStack>
                )}
                {props.children}
            </components.Menu>
        );
    }

    function MenuList(props: MenuListProps<T, boolean, G>): JSX.Element {
        return <components.MenuList {...props} className="w-full bg-transparent cursor-pointer space-y-0.5" />;
    }

    function Option(props: OptionProps<T, boolean, G>): JSX.Element {
        // Determine the content to render based on various conditions
        function renderContent(): JSX.Element {
            // If renderSelectedOption exists and option is selected, use it
            if (props.isSelected && renderSelectedOption) {
                return renderSelectedOption(props.data);
            }

            // If renderOption exists, use it for any state
            if (renderOption) {
                return (
                    <div
                        className={clsx(
                            props.isSelected && activeBackgroundClasses[color],
                            props.isFocused && !props.isSelected && focusedBackgroundClasses[color]
                        )}
                    >
                        {renderOption(props.data)}
                        {props.isSelected && <Icon icon={appIcons.solid.check} size={3.5} />}
                    </div>
                );
            }

            // Default rendering
            return (
                <HStack
                    justify="between"
                    className={clsx(
                        commonOptionClasses,
                        hoverBackgroundClasses[color],
                        props.isFocused && !props.isSelected && focusedBackgroundClasses[color],
                        props.isSelected ? activeBackgroundClasses[color] : inactiveOptionClasses
                    )}
                >
                    <Text size="xs" color="none">
                        {optionTitlePrefix ?? ''} {props.data[optionTitleKey] as string} {optionTitleSuffix ?? ''}
                    </Text>
                    {props.isSelected && <Icon icon={appIcons.solid.check} size={3.5} />}
                </HStack>
            );
        }

        return (
            <components.Option {...props} aria-selected={props.isSelected}>
                {renderContent()}
            </components.Option>
        );
    }

    function Placeholder(props: PlaceholderProps<T, boolean, G>): JSX.Element {
        return (
            <components.Placeholder
                {...props}
                className={
                    textColorClasses[
                        // eslint-disable-next-line no-nested-ternary
                        errorMessage ? 'danger' : (Array.isArray(value) ? value.length > 0 : Boolean(value)) ? 'primary' : 'quaternary'
                    ]
                }
            >
                {placeholder}
            </components.Placeholder>
        );
    }

    function Group(props: GroupProps<T, boolean, G>): JSX.Element {
        return (
            <components.Group {...props}>
                <div className="space-y-0.5">{props.children}</div>
            </components.Group>
        );
    }

    function GroupHeading(props: GroupHeadingProps<T, boolean, G>): JSX.Element {
        return (
            <components.GroupHeading {...props}>
                {renderGroupLabel ? (
                    renderGroupLabel(props.data)
                ) : (
                    <div className="px-3 pt-3">
                        <Text size="2xs" color={color}>
                            {props.data[optionGroupKey] as string}
                        </Text>
                    </div>
                )}
            </components.GroupHeading>
        );
    }

    function ValueContainer(props: ValueContainerProps<T, boolean, G>): JSX.Element {
        const selectedOptions = props.selectProps.value;
        const childrenArray = Children.toArray(props.children);

        if (Array.isArray(selectedOptions) && selectedOptions.length > maxDisplayedItems) {
            const displayChildren = childrenArray.slice(0, maxDisplayedItems);
            const remainingCount = selectedOptions.length - maxDisplayedItems;

            return (
                <components.ValueContainer {...props} className="overflow-x-auto flex-wrap-nowrap flex items-center">
                    {displayChildren}
                    <Text size="xs" className="ms-1">
                        +{remainingCount} {t('Common.More')}
                    </Text>
                </components.ValueContainer>
            );
        }

        return (
            <components.ValueContainer {...props} className="overflow-x-auto flex-wrap-nowrap flex items-center">
                {props.children}
            </components.ValueContainer>
        );
    }

    function NoOptionsMessage(props: NoticeProps<T, boolean, G>): JSX.Element {
        return (
            <components.NoOptionsMessage {...props} className="py-2">
                <VStack justify="center" alignment="center" hGap={3} className="py-8">
                    <Icon icon={noOptionsIcon ?? appIcons.outline.box} size={6} color="accent" />
                    <Text size="sm" weight="semibold" color="accent" className="mt-3">
                        {noOptionsTitle ?? t('Components.No_Options_Title')}
                    </Text>
                    <Text size="xs" color="tertiary" alignment="center" className="max-w-xs mt-2">
                        {noOptionsMessage ?? t('Components.No_Options_Message')}
                    </Text>
                </VStack>
            </components.NoOptionsMessage>
        );
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div ref={ref} className={clsx('relative space-y-1', composeWidthClasses(width), className)}>
            {/* Label */}
            {label && (
                <Text size="xs" weight="medium" color={errorMessage ? 'danger' : 'primary'} testId={testIds.select.label}>
                    {label} <span className="text-danger-600">{requiredIndicator}</span>
                </Text>
            )}

            {/* Input */}
            <HStack className={clsx('items-stretch', composeRadiusClasses(radius), composeShadowClasses(shadow), '-space-x-[1px]')}>
                <ReactSelect<T, boolean, G>
                    components={{
                        Control,
                        DropdownIndicator,
                        LoadingIndicator,
                        SelectContainer,
                        ClearIndicator,
                        Input,
                        Menu,
                        MenuList,
                        Option,
                        IndicatorsContainer,
                        Placeholder,
                        Group,
                        GroupHeading,
                        ValueContainer,
                        NoOptionsMessage,
                    }}
                    {...register}
                    options={options}
                    value={value}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    isClearable={isClearable}
                    hideSelectedOptions={false}
                    getOptionLabel={(option) => String(option[optionTitleKey])}
                    getOptionValue={(option) => String(option[optionIdKey])}
                    aria-label={label ?? placeholder}
                    aria-invalid={!!errorMessage}
                    aria-required={isRequired}
                    menuIsOpen={isMenuOpen}
                    menuPlacement={menuPlacement}
                    menuPortalTarget={menuPortalTarget}
                    tabSelectsValue
                    openMenuOnFocus
                    isSearchable
                    unstyled
                    onMenuOpen={() => {
                        setIsMenuOpen(true);
                    }}
                    onMenuClose={() => {
                        setIsMenuOpen(false);
                        if (register?.onBlur) {
                            register.onBlur({
                                target: {
                                    name: register.name,
                                    value,
                                },
                            });
                        }
                    }}
                    onBlur={() => {
                        if (register?.onBlur) {
                            register.onBlur({
                                target: {
                                    name: register.name,
                                    value,
                                },
                            });
                        }
                    }}
                    onChange={(newValue) => {
                        onChange?.(newValue as T);
                        if (register?.onChange) {
                            register.onChange({
                                target: {
                                    name: register.name,
                                    value: newValue,
                                },
                            });
                        }
                    }}
                />
            </HStack>

            {/* Error Message */}
            {errorMessage && (
                <Text size="2xs" color="danger" className="ms-2">
                    {errorMessage}
                </Text>
            )}
        </div>
    );
}
